<script lang="ts" setup generic="ListItemType extends GenericListItemType">
import type { GenericListHeading, GenericListItemType } from '@/core/models/generic-list.interface';

//#region Props & Emits
defineProps<{
   filters: { [filterKey: string]: string };
   hasResetFiltersButton: boolean;
   headings: Array<GenericListHeading<ListItemType>>;
   mode?: 'default' | 'light';
}>();
const emits = defineEmits<{
   filter: [{ key: string; value?: string | number }];
   resetFilters: [];
   toggleHeading: [{ index: number; value: boolean; refresh?: boolean }];
}>();
//#endregion

//#region Filter
function filter(params: { key: string; value?: string | number }) {
   const { key, value } = params;
   emits('filter', { key, value });
}

function resetFilters() {
   emits('resetFilters');
}
//#endregion
</script>

<template>
   <div class="generic-list-filters" :class="{ [`-${mode}`]: true }">
      <slot name="filters" v-bind="{ filter, query: filters, resetFilters }"></slot>

      <NxtIconButton v-if="hasResetFiltersButton" :name="'undo'" :hover-text="$t('actions.reset-filters')" @on-click="resetFilters" />

      <!-- optional headings -->
      <NxtHoverCard
         v-if="hasResetFiltersButton && !!headings.find((h) => h.optional || h.temp_optional)"
         :mode="'light'"
         :with-triangle="false"
         :clickable="true"
         :is-card-hoverable="true"
      >
         <template #hover-item>
            <NxtIconButton :name="'table'" />
         </template>

         <div class="d-flex flex-column gap-4 p-2">
            <NxtCheckbox
               v-for="(heading, index) in headings.map((h, i) => ({ ...h, realIndex: i })).filter((h) => h.optional || h.temp_optional)"
               :key="index"
               :model-value="heading.enabled"
               :name="`heading-${heading.label}-${index}`"
               :label="heading.label"
               @update:model-value="emits('toggleHeading', { index: heading.realIndex, value: $event })"
            />
         </div>
      </NxtHoverCard>

      <slot name="filterAddons"></slot>
   </div>
</template>

<style lang="scss" scoped>
.generic-list-filters {
   display: flex;
   align-items: center;
   gap: var(--nxt-gutter);

   background-color: inherit;

   border-bottom: 1px solid var(--nxt-grey-200);
   padding-bottom: var(--nxt-gutter);

   &.-light {
      background-color: var(--surface-container-1);
   }

   &:deep(.nxt-form-group) {
      flex: 1;
      max-width: toRem(200);
   }

   @include xs {
      flex-wrap: wrap;
   }
}
</style>
